import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, CheckCircle2, ArrowRight, ArrowLeft, Building2, Compass, Users, ImagePlus, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";

const dollars = (c) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

const PARTNER_TYPES = [
  { id: "brand", label: "Brand / Product Business", icon: Building2, blurb: "Gear makers and retailers selling products to hunters." },
  { id: "outfitter", label: "Outfitter", icon: Compass, blurb: "Guided hunt operators offering trips and packages." },
  { id: "guide", label: "Guide", icon: Users, blurb: "Independent guides building a personal hunting brand." },
];

const STEPS = ["type", "subscribe", "profile", "preview", "done"];
const STEP_LABEL = { type: "Partner type", subscribe: "Subscription", profile: "Profile", preview: "Preview", done: "Publish" };

function Stepper({ current }) {
  const idx = STEPS.indexOf(current === "done" ? "done" : current);
  return (
    <div className="flex items-center gap-2 sm:gap-3 flex-wrap" data-testid="onboarding-stepper">
      {STEPS.map((s, i) => {
        const active = i === idx;
        const done = i < idx;
        return (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${active ? "bg-blaze text-white" : done ? "bg-forest text-bone" : "bg-paper border border-saddle/25 text-charcoal/50"}`} data-testid={`step-dot-${s}`}>
              {done ? <CheckCircle2 size={15} /> : i + 1}
            </span>
            <span className={`text-xs font-semibold ${active ? "text-forest" : "text-charcoal/50"} hidden sm:inline`}>{STEP_LABEL[s]}</span>
            {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-saddle/25" />}
          </div>
        );
      })}
    </div>
  );
}

function TypeStep({ onNext, busy, initialType, initialName }) {
  const [name, setName] = useState(initialName || "");
  const [type, setType] = useState(initialType || "");
  return (
    <div data-testid="step-type">
      <h2 className="font-serif text-3xl text-forest">What kind of partner are you?</h2>
      <p className="text-charcoal/60 mt-2 text-sm">Choose the profile that best fits your operation. State partners are set up by the HomeRange team.</p>
      <label className="block mt-6 text-[11px] font-bold uppercase tracking-wide text-saddle">Partner / business name</label>
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Timberline Optics"
        className="w-full mt-1.5 border border-saddle/25 bg-white px-4 py-3 rounded-lg text-charcoal" data-testid="onboarding-name-input" />
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {PARTNER_TYPES.map((t) => (
          <button key={t.id} onClick={() => setType(t.id)}
            className={`text-left p-5 rounded-xl border transition-colors ${type === t.id ? "border-blaze bg-blaze/5 ring-1 ring-blaze" : "border-saddle/20 bg-white hover:border-forest"}`}
            data-testid={`type-${t.id}`}>
            <t.icon size={22} className={type === t.id ? "text-blaze" : "text-forest"} />
            <p className="font-semibold text-forest mt-3">{t.label}</p>
            <p className="text-xs text-charcoal/60 mt-1 leading-relaxed">{t.blurb}</p>
          </button>
        ))}
      </div>
      <button disabled={!name.trim() || !type || busy} onClick={() => onNext({ name: name.trim(), partner_type: type })}
        className="mt-8 inline-flex items-center gap-2 bg-forest text-bone px-6 py-3 rounded-full font-semibold disabled:opacity-50 hover:bg-forest-2 transition-colors" data-testid="onboarding-type-continue">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <>Continue <ArrowRight size={16} /></>}
      </button>
    </div>
  );
}

function SubscribeStep({ status, plans, onSubscribe, busy, chosen }) {
  const active = status?.active;
  if (active) {
    return (
      <div data-testid="step-subscribe">
        <h2 className="font-serif text-3xl text-forest">Subscription active</h2>
        <div className="mt-4 flex items-center gap-2 text-emerald-700 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg" data-testid="subscribe-active-banner">
          <CheckCircle2 size={18} /> <span className="font-semibold capitalize">{status.subscription_status} plan — you&apos;re ready to build your profile.</span>
        </div>
        <button onClick={() => onSubscribe(null)} className="mt-6 inline-flex items-center gap-2 bg-forest text-bone px-6 py-3 rounded-full font-semibold hover:bg-forest-2 transition-colors" data-testid="onboarding-subscribe-continue">
          Continue to profile <ArrowRight size={16} />
        </button>
      </div>
    );
  }
  return (
    <div data-testid="step-subscribe">
      <h2 className="font-serif text-3xl text-forest">Choose your partner plan</h2>
      <p className="text-charcoal/60 mt-2 text-sm">Your HomeRange partner profile, unique URL, QR code and referral tools are included on every plan. Higher tiers unlock better placement and lower marketplace fees.</p>
      <div className="grid sm:grid-cols-3 gap-4 mt-6">
        {["business", "pro", "elite"].map((id) => plans[id] && (
          <div key={id} className={`p-6 rounded-xl border flex flex-col ${id === "pro" ? "border-blaze bg-forest text-bone" : "border-saddle/20 bg-white"}`} data-testid={`onboarding-plan-${id}`}>
            <h3 className={`font-serif text-2xl ${id === "pro" ? "text-bone" : "text-forest"}`}>{plans[id].name.replace("HomeRange ", "")}</h3>
            <div className="mt-2"><span className="font-serif text-3xl tabular-nums">{dollars(plans[id].price_cents)}</span><span className={id === "pro" ? "text-bone/60" : "text-charcoal/50"}>/mo</span></div>
            <p className={`text-xs mt-1 ${id === "pro" ? "text-blaze" : "text-saddle"}`}>{plans[id].fee_pct}% marketplace fee</p>
            <ul className="mt-4 space-y-1.5 flex-1">
              {plans[id].features.slice(0, 5).map((f, i) => <li key={i} className={`text-xs flex items-start gap-1.5 ${id === "pro" ? "text-bone/80" : "text-charcoal/70"}`}><CheckCircle2 size={12} className={`${id === "pro" ? "text-blaze" : "text-forest"} mt-0.5 shrink-0`} /> {f}</li>)}
            </ul>
            <button onClick={() => onSubscribe(id)} disabled={busy}
              className={`mt-5 py-2.5 rounded-full text-sm font-semibold transition-colors disabled:opacity-60 ${id === "pro" ? "bg-blaze text-white hover:bg-blaze-dark" : "bg-forest text-bone hover:bg-forest-2"}`}
              data-testid={`onboarding-subscribe-${id}`}>
              {busy && chosen === id ? <Loader2 size={15} className="animate-spin inline" /> : `Choose ${plans[id].name.replace("HomeRange ", "")}`}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProfileStep({ partner, onSave, onNext, busy }) {
  const b = partner?.business || {};
  const [form, setForm] = useState({
    tagline: b.tagline || "", about: b.about || "", location: b.location || "",
    website: b.website || "", email: b.email || "", phone: b.phone || "",
    cta_label: b.cta_label || "", cta_url: b.cta_url || "",
    states_served: (b.states_served || []).join(", "),
  });
  const [uploading, setUploading] = useState(false);
  const completion = partner?.partner?.completion ?? 0;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadImg = async (kind, file) => {
    if (!file) return;
    setUploading(true);
    try {
      if (kind === "logo") await api.uploadBusinessLogo(file);
      else await api.uploadBusinessCover(file);
      toast.success(`${kind === "logo" ? "Logo" : "Cover"} uploaded`);
      onSave({}, true); // refetch only
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };
  const save = () => onSave({
    ...form,
    states_served: form.states_served.split(",").map((s) => s.trim()).filter(Boolean),
  });

  return (
    <div data-testid="step-profile">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-serif text-3xl text-forest">Build your partner profile</h2>
        <div className="text-right">
          <span className="text-sm font-semibold text-forest" data-testid="profile-completion-pct">{completion}% complete</span>
          <div className="h-2 w-40 bg-paper rounded-full mt-1 overflow-hidden"><div className="h-full bg-blaze transition-all" style={{ width: `${completion}%` }} /></div>
        </div>
      </div>
      <p className="text-charcoal/60 mt-2 text-sm">Complete at least 60% to publish. The more you add, the better hunters find you.</p>

      <div className="flex gap-4 mt-6">
        <label className="flex-1 border border-dashed border-saddle/40 rounded-lg p-4 text-center cursor-pointer hover:border-forest transition-colors" data-testid="upload-logo">
          <ImagePlus size={20} className="mx-auto text-saddle" />
          <span className="block text-xs font-semibold text-charcoal/70 mt-2">{b.logo ? "Logo added ✓" : "Upload logo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg("logo", e.target.files?.[0])} />
        </label>
        <label className="flex-1 border border-dashed border-saddle/40 rounded-lg p-4 text-center cursor-pointer hover:border-forest transition-colors" data-testid="upload-cover">
          <ImagePlus size={20} className="mx-auto text-saddle" />
          <span className="block text-xs font-semibold text-charcoal/70 mt-2">{b.cover ? "Cover added ✓" : "Upload cover photo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadImg("cover", e.target.files?.[0])} />
        </label>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        <Field label="Tagline" v={form.tagline} on={set("tagline")} testid="f-tagline" />
        <Field label="Location" v={form.location} on={set("location")} testid="f-location" />
        <Field label="States served (comma-separated)" v={form.states_served} on={set("states_served")} testid="f-states" />
        <Field label="Website" v={form.website} on={set("website")} testid="f-website" />
        <Field label="Contact email" v={form.email} on={set("email")} testid="f-email" />
        <Field label="Phone" v={form.phone} on={set("phone")} testid="f-phone" />
        <Field label="Button label (e.g. Book a hunt)" v={form.cta_label} on={set("cta_label")} testid="f-cta-label" />
        <Field label="Button link" v={form.cta_url} on={set("cta_url")} testid="f-cta-url" />
      </div>
      <div className="mt-4">
        <label className="text-[11px] font-bold uppercase tracking-wide text-saddle">About</label>
        <textarea value={form.about} onChange={set("about")} rows={4} className="w-full mt-1.5 border border-saddle/25 bg-white px-4 py-3 rounded-lg text-charcoal" data-testid="f-about" />
      </div>

      <div className="flex items-center gap-3 mt-6">
        <button onClick={save} disabled={busy || uploading} className="inline-flex items-center gap-2 border border-forest text-forest px-5 py-2.5 rounded-full font-semibold disabled:opacity-50 hover:bg-forest hover:text-bone transition-colors" data-testid="onboarding-profile-save">
          {busy || uploading ? <Loader2 size={15} className="animate-spin" /> : "Save profile"}
        </button>
        <button onClick={onNext} disabled={completion < 60} className="inline-flex items-center gap-2 bg-forest text-bone px-6 py-2.5 rounded-full font-semibold disabled:opacity-50 hover:bg-forest-2 transition-colors" data-testid="onboarding-profile-continue">
          Preview <ArrowRight size={16} />
        </button>
        {completion < 60 && <span className="text-xs text-charcoal/50">Add a bit more to reach 60%</span>}
      </div>
    </div>
  );
}

function Field({ label, v, on, testid }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-wide text-saddle">{label}</label>
      <input value={v} onChange={on} className="w-full mt-1.5 border border-saddle/25 bg-white px-4 py-2.5 rounded-lg text-charcoal" data-testid={testid} />
    </div>
  );
}

function PreviewStep({ partner, onPublish, busy }) {
  const p = partner?.partner || {};
  const b = partner?.business || {};
  return (
    <div data-testid="step-preview">
      <h2 className="font-serif text-3xl text-forest">Preview & publish</h2>
      <p className="text-charcoal/60 mt-2 text-sm">This is how your profile will appear on HomeRange. You can keep editing after publishing.</p>
      <div className="mt-6 border border-saddle/20 rounded-xl overflow-hidden bg-white" data-testid="preview-card">
        <div className="h-28 bg-charcoal overflow-hidden">{b.cover && <img src={b.cover} alt="" className="w-full h-full object-cover" />}</div>
        <div className="p-5 flex gap-4 -mt-10">
          <div className="w-20 h-20 rounded-xl bg-white border border-saddle/20 shadow overflow-hidden grid place-items-center shrink-0">
            {b.logo ? <img src={b.logo} alt="" className="w-full h-full object-cover" /> : <span className="font-serif text-2xl text-forest">{(b.name || "H")[0]}</span>}
          </div>
          <div className="pt-9">
            <h3 className="font-serif text-2xl text-forest">{b.name}</h3>
            {b.tagline && <p className="text-sm text-charcoal/70 italic">{b.tagline}</p>}
            <p className="text-xs text-charcoal/50 mt-1">{p.public_path}</p>
          </div>
        </div>
        {b.about && <p className="px-5 pb-5 text-sm text-charcoal/75 whitespace-pre-wrap">{b.about}</p>}
      </div>
      <div className="mt-4 flex items-center gap-2 text-xs text-charcoal/60">
        <ShieldCheck size={14} className="text-forest" /> Verification badges are granted by the HomeRange team after review — your profile publishes as &quot;pending&quot;.
      </div>
      <button onClick={onPublish} disabled={busy} className="mt-6 inline-flex items-center gap-2 bg-blaze text-white px-8 py-3 rounded-full font-semibold disabled:opacity-50 hover:bg-blaze-dark transition-colors" data-testid="onboarding-publish-btn">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <><Sparkles size={16} /> Publish my profile</>}
      </button>
    </div>
  );
}

export default function PartnerOnboarding() {
  const { isAuthed, ready } = useAuth();
  const { setAuthOpen } = useUI();
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [partner, setPartner] = useState(null);
  const [plans, setPlans] = useState({});
  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [chosen, setChosen] = useState(null);

  const load = useCallback(async () => {
    const [ob, me] = await Promise.all([api.partnerOnboarding(), api.partnerMe().catch(() => ({ partner: null }))]);
    setStatus(ob);
    setPartner(me.partner ? me : { partner: ob.partner, business: {} });
    setStep((prev) => prev || (ob.step === "done" ? "preview" : ob.step === "type" && !ob.has_business ? "type" : ob.step));
    if (ob.onboarding_complete && ob.step === "done") navigate("/partner/dashboard", { replace: true });
    return ob;
  }, [navigate]);

  useEffect(() => {
    if (!ready) return;
    if (!isAuthed) return;
    api.marketplacePlans().then(setPlans).catch(() => {});
    load();
  }, [ready, isAuthed, load]);

  if (!ready) return <Shell><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></Shell>;
  if (!isAuthed) return (
    <Shell>
      <div className="max-w-md mx-auto text-center py-24" data-testid="onboarding-signin-gate">
        <h1 className="font-serif text-3xl text-forest">Become a HomeRange Partner</h1>
        <p className="text-charcoal/60 mt-2">Sign in or create your account to start onboarding.</p>
        <button onClick={() => setAuthOpen(true)} className="mt-5 bg-blaze text-white px-6 py-3 rounded-full font-semibold" data-testid="onboarding-signin-btn">Sign in to continue</button>
      </div>
    </Shell>
  );
  if (!status) return <Shell><div className="py-32 grid place-items-center"><Loader2 className="animate-spin text-forest" /></div></Shell>;

  const handleType = async ({ name, partner_type }) => {
    setBusy(true);
    try { await api.partnerSetup({ name, partner_type, category: "Other" }); await load(); setStep("subscribe"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const handleSubscribe = async (planId) => {
    if (!planId) { setStep("profile"); return; }
    setBusy(true); setChosen(planId);
    try { const res = await api.subscribe({ plan: planId, origin_url: window.location.origin }); window.location.href = res.checkout_url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start checkout"); setBusy(false); setChosen(null); }
  };
  const handleProfileSave = async (patch, refetchOnly = false) => {
    setBusy(true);
    try { if (!refetchOnly) await api.partnerSetup(patch); await load(); if (!refetchOnly) toast.success("Profile saved"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBusy(false);
  };
  const handlePublish = async () => {
    setBusy(true);
    try { await api.partnerOnboardingPublish(); toast.success("Your partner profile is live!"); navigate("/partner/dashboard"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not publish"); setBusy(false); }
  };

  return (
    <Shell>
      <div className="max-w-3xl mx-auto">
        <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-blaze">Partner Onboarding</span>
        <div className="mt-4 mb-8"><Stepper current={step} /></div>
        <motion.div key={step} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
          {step === "type" && <TypeStep onNext={handleType} busy={busy} initialType={status.partner_type} initialName={partner?.business?.name} />}
          {step === "subscribe" && <SubscribeStep status={status} plans={plans} onSubscribe={handleSubscribe} busy={busy} chosen={chosen} />}
          {step === "profile" && <ProfileStep partner={partner} onSave={handleProfileSave} onNext={() => setStep("preview")} busy={busy} />}
          {step === "preview" && <PreviewStep partner={partner} onPublish={handlePublish} busy={busy} />}
        </motion.div>
        {step && step !== "type" && (
          <button onClick={() => { const order = ["type", "subscribe", "profile", "preview"]; const i = order.indexOf(step); if (i > 0) setStep(order[i - 1]); }}
            className="mt-8 inline-flex items-center gap-1.5 text-sm text-charcoal/50 hover:text-forest" data-testid="onboarding-back">
            <ArrowLeft size={14} /> Back
          </button>
        )}
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="bg-paper min-h-screen" data-testid="partner-onboarding">
      <SiteHeader />
      <main className="max-w-[1100px] mx-auto px-5 sm:px-8 py-10">{children}</main>
      <Footer />
    </div>
  );
}
